import _ from 'underscore';
import { getCurrencyText } from '@bingads-webui-react/currency';

import { recommendationConfigs } from '../recommendation-configs';
import {
  ESTIMATE_TYPES,
  ESTIMATE_STRINGS,
  RECOMMENDATION_TYPES,
  MATCH_TYPE_IDS,
  NO_SYMBOL_ESTIMATE_TYPES,
  FAC_RECOMMENDATION_CONFIG,
} from '../consts';
import {
  formatPercentHundredths,
  formatCost,
  formatDecimal,
  formatInteger,
} from './basic-format-data';
import { isCompetition } from '../util';

// place format function using recommendation-configs in this file

/**
 * Format estimation value based on estimation type
 * @param {string} type - estimation type
 * @param {number} value - value to be formatted
 * @param {Object} i18n - Localization component
 * @param {string} currency - currency code
 * @return {string} formatted estimation value
 */
const formatEstimateValue = (type, value, i18n, currency) => {
  switch (type) {
    case ESTIMATE_TYPES.IMPRESSION_SHARE:
    case ESTIMATE_TYPES.CTR:
      return formatPercentHundredths(value, i18n); // 12.34%
    case ESTIMATE_TYPES.COST:
      return formatCost(value, i18n, currency); // $32.00K
    case ESTIMATE_TYPES.REJECTED_ADS:
    case ESTIMATE_TYPES.REJECTED_KEYWORDS:
      return formatInteger(value, i18n);
    case ESTIMATE_TYPES.IMPRESSIONS:
    case ESTIMATE_TYPES.QUERY_VOLUME:
    case ESTIMATE_TYPES.CLICKS:
    case ESTIMATE_TYPES.WEEKLY_SEARCHES:
    case ESTIMATE_TYPES.MISSING_CONVERSIONS:
    case ESTIMATE_TYPES.TARGETED_PRODUCTS:
    default:
      return formatDecimal(value, i18n); // 12.3M
  }
};

/**
 * Format estimation block
 * @param {Object} options - options
 * @param {string} options.type - recomendation type
 * @param {Object} options.i18n - Localization component
 * @param {Object} options.estimates - estimate value collection
 * @param {string} options.currency - currency code
 * @param {string} options.inDetailsView - estimates block will be shown in details view or not
 * @return {Object} formatted estimation block
 */
export const formatEstimates = ({
  type, i18n, estimates, currency, inDetailsView,
}) => {
  if (!estimates) {
    return null;
  }

  const {
    isPrimaryFromToEnabled,
    primaryIncreaseNullable,
  } = recommendationConfigs[type];
  let {
    primaryEstimateType,
    secondPrimaryEstimateType,
    isDoublePrimary,
    secondaryEstimateType,
    costEstimateType,
    isEstimate,
  } = recommendationConfigs[type];
  let tertiaryEstimateType = null;

  const bindedFormatValue = _.partial(formatEstimateValue, _, _, i18n, currency);
  if (!primaryEstimateType) {
    switch (type) {
      case RECOMMENDATION_TYPES.BUDGET_OPPORTUNITY:
      case RECOMMENDATION_TYPES.ADJUST_SHARED_BUDGET_OPPORTUNITY:
        primaryEstimateType = estimates.primaryEstimate;
        secondaryEstimateType = estimates.primaryEstimate === ESTIMATE_TYPES.CONVERSIONS ?
          ESTIMATE_TYPES.CLICKS : ESTIMATE_TYPES.COST;
        costEstimateType = estimates.primaryEstimate === ESTIMATE_TYPES.CONVERSIONS ?
          ESTIMATE_TYPES.COST : null;
        break;
      case RECOMMENDATION_TYPES.FIX_AD_TEXT:
      case RECOMMENDATION_TYPES.FIX_AD_DESTINATION:
        primaryEstimateType = estimates.primaryEstimate;
        secondPrimaryEstimateType = estimates.secondPrimaryEstimate;
        isDoublePrimary = estimates.isDoublePrimary; // eslint-disable-line prefer-destructuring
        break;
      default:
        break;
    }
  }

  // Special logic for competition
  // Show Est. Conversions Increase as secondary estimate in summary page
  // Show Est. Conversions Increase as tertiary estimate in summary page
  if (isCompetition(type) && estimates.convIncrease) {
    if (inDetailsView) {
      tertiaryEstimateType = ESTIMATE_TYPES.CONVERSIONS;
    } else {
      secondaryEstimateType = ESTIMATE_TYPES.CONVERSIONS;
    }
  }
  const primaryString = i18n.getString(ESTIMATE_STRINGS[primaryEstimateType]);

  // We get CTR (%) from MT - 'CtrLift' field. Increase field is applied for calculation only.
  const primaryIncreaseNumber = (primaryEstimateType === ESTIMATE_TYPES.CTR) ?
    estimates[`${primaryEstimateType}Lift`] : estimates[`${primaryEstimateType}Increase`];
  const primaryIncrease = bindedFormatValue(primaryEstimateType, primaryIncreaseNumber);

  const increaseSymbol = primaryEstimateType === ESTIMATE_TYPES.CTR ? '↑' : '+';
  const primaryIncreaseSymbol = (primaryIncreaseNumber >= 0 &&
    !_.contains(NO_SYMBOL_ESTIMATE_TYPES, primaryEstimateType)) ? increaseSymbol : '';

  const primaryPts = primaryEstimateType === ESTIMATE_TYPES.IMPRESSION_SHARE ?
    i18n.getString('Summary_Card_Estimate_Points') : '';

  const hasFrom = isPrimaryFromToEnabled && !_.isUndefined(estimates[`${primaryEstimateType}Current`]);
  const primaryFrom = hasFrom && bindedFormatValue(primaryEstimateType, estimates[`${primaryEstimateType}Current`]);
  const primaryFromString = hasFrom && i18n.getString('Summary_Card_Estimate_Primary_From');
  const primaryTo = hasFrom
    && bindedFormatValue(primaryEstimateType, estimates[`${primaryEstimateType}Current`] + estimates[`${primaryEstimateType}Increase`]);
  const primaryToString = hasFrom && i18n.getString('Summary_Card_Estimate_Primary_To');
  const isPositivePrimary = !(primaryEstimateType === ESTIMATE_TYPES.COST &&
    primaryIncreaseNumber > 0) && !_.contains(NO_SYMBOL_ESTIMATE_TYPES, primaryEstimateType);

  const secondaryIncrease = secondaryEstimateType
    && bindedFormatValue(secondaryEstimateType, estimates[`${secondaryEstimateType}Increase`]);
  const secondaryString = secondaryEstimateType
    && i18n.getString(ESTIMATE_STRINGS[secondaryEstimateType]);
  const isPositiveSecondary = secondaryEstimateType &&
    !(secondaryEstimateType === ESTIMATE_TYPES.COST && estimates[`${secondaryEstimateType}Increase`] > 0);

  const tertiaryIncrease = tertiaryEstimateType
    && bindedFormatValue(tertiaryEstimateType, estimates[`${tertiaryEstimateType}Increase`]);
  const tertiaryString = tertiaryEstimateType
    && i18n.getString(ESTIMATE_STRINGS[tertiaryEstimateType]);

  const spendIncrease = costEstimateType
    && bindedFormatValue(costEstimateType, estimates[`${costEstimateType}Increase`]);
  const spendString = costEstimateType && i18n.getString(ESTIMATE_STRINGS[costEstimateType]);

  const estimateNote = i18n.getString('Summary_Card_Estimate_Note');

  const showEstimates = primaryIncreaseNumber !== 0 ||
    (type !== RECOMMENDATION_TYPES.CALLOUT && type !== RECOMMENDATION_TYPES.SITE_LINK);

  let secondPrimaryString;
  let secondPrimaryIncrease;

  if (isDoublePrimary) {
    secondPrimaryString = i18n.getString(ESTIMATE_STRINGS[secondPrimaryEstimateType]);

    // We get CTR (%) from MT - 'CtrLift' field. Increase field is applied for calculation only.
    const secondPrimaryIncreaseNumber = (primaryEstimateType === ESTIMATE_TYPES.CTR) ?
      estimates[`${secondPrimaryEstimateType}Lift`] :
      estimates[`${secondPrimaryEstimateType}Increase`];
    secondPrimaryIncrease = bindedFormatValue(
      secondPrimaryEstimateType,
      secondPrimaryIncreaseNumber
    );
  }

  if (type === RECOMMENDATION_TYPES.DYNAMIC_SEARCH_ADS && primaryIncreaseNullable) {
    isEstimate = primaryIncrease !== '0';
  }

  const formatted = {
    primaryIncrease,
    primaryPts,
    primaryFrom,
    primaryTo,
    primaryFromString,
    primaryToString,
    primaryString,
    primaryIncreaseSymbol,
    secondPrimaryIncrease,
    secondPrimaryString,
    isDoublePrimary,
    isPositivePrimary,
    secondaryIncrease,
    secondaryString,
    isPositiveSecondary,
    spendIncrease,
    spendString,
    estimateNote,
    showEstimates,
    isEstimate,
  };

  if (tertiaryEstimateType) {
    formatted.tertiaryIncrease = tertiaryIncrease;
    formatted.tertiaryString = tertiaryString;
  }

  if (primaryIncreaseNullable) {
    formatted.primaryIncreaseNullable = primaryIncreaseNullable;
  }

  return formatted;
};

export const formatLocaledSourceType = (i18n, source) => {
  const convertedSource = `Recommendation_Keywword_Source_Type_${source}`;
  return i18n.getString(convertedSource) || source;
};

export const formatLocaledSeasonalEventType = (i18n, seasonEvent) => {
  const arr = seasonEvent.split(' ');
  let convertedSeasonEvent = 'Recommendation_Keywword_Seasonal_Event_Type';
  _.each(arr, (word) => {
    convertedSeasonEvent += `_${word.charAt(0).toUpperCase() + word.slice(1)}`;
  });
  convertedSeasonEvent = convertedSeasonEvent.replace('\'', '');
  return i18n.getString(convertedSeasonEvent) || seasonEvent;
};

export const formatPictchingSource = (i18n, pitchingKey, sources, seasonalEvent) => {
  let value = '';
  if (_.isUndefined(sources) || sources.length === 0) {
    return '';
  }

  if (sources.length === 1) {
    value = i18n.getString(
      pitchingKey,
      {
        source1: formatLocaledSourceType(i18n, sources[0]),
      }
    );
  } else if (sources.length === 2) {
    value = i18n.getString(
      `${pitchingKey}_Two`,
      {
        source1: formatLocaledSourceType(i18n, sources[0]),
        source2: formatLocaledSourceType(i18n, sources[1]),
      }
    );
  } else if (sources.length >= 3) {
    value = i18n.getString(
      `${pitchingKey}_Three`,
      {
        source1: formatLocaledSourceType(i18n, sources[0]),
        source2: formatLocaledSourceType(i18n, sources[1]),
        source3: formatLocaledSourceType(i18n, sources[2]),
      }
    );
  }

  if (!_.isUndefined(seasonalEvent) && seasonalEvent.length > 0) {
    value += ` ${i18n.getString(
      `${pitchingKey}_Seasonal_Event`,
      {
        seasonalEvent: formatLocaledSeasonalEventType(i18n, seasonalEvent[0].SampleContent),
      }
    )}`;
  }
  return value;
};

export const formatKeyword = (keyword, matchType) => {
  switch (matchType) {
    case MATCH_TYPE_IDS.EXACT:
      return `[${keyword}]`;
    case MATCH_TYPE_IDS.PHRASE:
      return `"${keyword}"`;
    default:
      return keyword;
  }
};

/**
 * Format coupon block
 * @param {Object} options - options
 * @param {Object} options.i18n - Localization component
 * @param {Object} options.coupon - coupon value collection
 * @param {string} options.currency - currency code
 * @return {Object} formatted coupon block
 */
export const formatCoupon = ({
  i18n, coupon, currency,
}) => {
  const couponConfig = FAC_RECOMMENDATION_CONFIG[coupon.FeatureId.toString()];

  if (!couponConfig) {
    return null;
  }

  const {
    couponTitleActionKey,
    couponDescriptionKey,
    couponDescriptionTitleKey,
    couponTagMessageKey,
    couponLightboxTitle,
  } = couponConfig;

  const titleAction = i18n.getString(couponTitleActionKey);
  const descriptionTitle = i18n.getString(couponDescriptionTitleKey);
  const description = i18n.getString(couponDescriptionKey);
  const tagMessage = i18n.getString(couponTagMessageKey);

  return {
    value: getCurrencyText({
      currencyCode: currency,
      amount: Math.floor(coupon.CouponValue),
      i18n,
      maxFractionDigits: 0,
      hideCurrencyCode: true,
    }),
    cost: getCurrencyText({
      currencyCode: currency,
      amount: Math.ceil(coupon.CouponUpfrontSpend),
      i18n,
      maxFractionDigits: 0,
      hideCurrencyCode: true,
    }),
    expirationDate: new Date(coupon.EndDate).toLocaleDateString(),
    descriptionTitle,
    descriptionShort: descriptionTitle,
    titleAction,
    description,
    tagMessage,
    rawData: coupon,
    couponLightboxTitle,
  };
};
